import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { Email } from '@kula-ai/components';
import type { EmailCompositionValue, SelectOption } from '@kula-ai/components';
import { useSelector } from 'react-redux';
import { uploadUserInlineImage } from '@/api/userImageUploads/useUploadUserInlineImage';
import { customTemplateSelectProps } from '@/components/shared/customTemplateLabel';
import {
  useCcBccUserOptions,
  useSenderAutocomplete,
  asyncSelectProps,
  mergeOptionsByValue,
} from '@/api/userSearch/useCcBccUserOptions';
import { isTemplateEdited } from './templateSelection';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { selectUsers } from '@/store/users/selectors';
import { useEmailComposer } from './useEmailComposer';
import { toSelectOptions } from './emailValue';
import type { EmailUser, EmailValueKey } from './emailValue';

interface EmailComposerProps<TPatch extends Record<string, unknown>> {
  formik?: any;
  toValue: (_values: any) => EmailCompositionValue;
  toPatch: (
    _value: EmailCompositionValue,
    _resolveUser: (_value: string) => EmailUser | undefined
  ) => TPatch;
  /** Defaults to the delegatable senders; pass to restrict who may send. */
  fromUsers?: EmailUser[];
  /** Defaults to every user. */
  ccBccUsers?: EmailUser[];
  /** Recipients already saved on the record, so their labels survive (ENG-3766). */
  savedUsers?: EmailUser[];
  valueKey?: EmailValueKey;
  // Hook seams — see `useEmailComposer`.
  applyPatch?: (_patch: Record<string, unknown>) => void;
  seedKey?: unknown;
  normalise?: (
    _prev: EmailCompositionValue,
    _next: EmailCompositionValue
  ) => EmailCompositionValue;
  onTemplateChange?: (
    _templateId: string | null,
    _previousId: string | null,
    _patch: TPatch
  ) => Record<string, unknown> | void;
  /** Sender adopted once the list confirms it is selectable. */
  wantedFrom?: string;
  /** Template whose subject and body are applied when its id changes. */
  selectedTemplate?: {
    id?: string | number;
    subject?: string;
    body?: string;
  } | null;

  /** Supplying options moves the template picker into the card. */
  templateOptions?: SelectOption[];
  templateSelectProps?: Record<string, unknown>;
  /** Recipients the surface picks itself; omit for a candidate-fixed email. */
  toOptions?: SelectOption[];
  toMode?: 'single' | 'multiple';
  /** Merged over the editor defaults. */
  toolbarFeatures?: Record<string, boolean>;
  variables?: Record<string, string[]>;
  subjectMaxLength?: number;
  /** Overrides `submitCount`, for surfaces that gate their own error display. */
  submitAttempt?: number;
  /** Surfaces that mirror kula's inline errors into their own footer. */
  onInlineErrorChange?: (_errors: unknown) => void;
  /** Attachment chips and handlers — see `useEmailAttachments`. */
  attachments?: Record<string, unknown>;
  extraExtensions?: unknown[];
  toolbarStartSlot?: ReactNode;
  /** Rendered inside the email card below the editor, sharing its border chrome. */
  footerAddon?: ReactNode;
  onEditorReady?: (_editor: unknown) => void;
  editorPlaceholder?: string;
  disabled?: boolean;
}

// Every surface renders these identically; they are the composer's shape, not
// a per-surface choice.
const PLACEHOLDERS = {
  from: 'Select from',
  to: 'Select recipients',
  subject: 'Enter subject',
  template: 'Select email template',
} as const;

/**
 * The shared kula `Email` composer.
 *
 * Owns the state machine (via `useEmailComposer`) and the props every surface
 * rendered the same way, so a surface supplies only its mappers, its toolbar
 * and whatever it genuinely does differently.
 */
export const EmailComposer = <TPatch extends Record<string, unknown>>({
  formik,
  toValue,
  toPatch,
  fromUsers,
  ccBccUsers,
  savedUsers,
  valueKey,
  applyPatch,
  seedKey,
  normalise,
  onTemplateChange,
  wantedFrom,
  selectedTemplate,
  templateOptions,
  templateSelectProps,
  toOptions,
  toMode = 'multiple',
  toolbarFeatures,
  variables,
  subjectMaxLength,
  submitAttempt,
  onInlineErrorChange,
  attachments,
  extraExtensions,
  toolbarStartSlot,
  footerAddon,
  onEditorReady,
  editorPlaceholder = 'Start typing...',
  disabled = false,
}: EmailComposerProps<TPatch>) => {
  // Every surface wired these two identically, so they are the default here.
  const { fromUsers: delegatableSenders } = useGetAllUsers();
  const allUsers = useSelector(selectUsers);

  // CC/BCC is searched server-side instead of filtered over the redux copy of
  // the account, which is a page of users and never the whole list (ENG-3766).
  //
  // Those users still go in: in async mode the menu comes from `loadOptions`,
  // while the selected chips resolve their labels from this list. Surfaces
  // that store cc/bcc as bare ids have nothing else to resolve them with, so
  // dropping it rendered saved recipients as raw ids.
  const knownCcBccOptions = useMemo(
    () =>
      toSelectOptions(
        [
          ...(ccBccUsers ?? (allUsers as EmailUser[]) ?? []),
          ...(savedUsers ?? []),
        ],
        valueKey
      ),
    [ccBccUsers, allUsers, savedUsers, valueKey]
  );
  const { initialOptions: searchedCcBccOptions, loadOptions: loadCcBcc } =
    useCcBccUserOptions(knownCcBccOptions, valueKey);
  const ccBccSelectProps = useMemo(
    () => asyncSelectProps(loadCcBcc),
    [loadCcBcc]
  );

  // From searches the account too, the way the interview emails do. The
  // delegatable list is what the field opens on; `searchedSenders` feeds back
  // into the composer so a searched pick still resolves to a user object —
  // without it a stale `fromUserObject` would outlive the selection.
  const senders = useMemo(
    () => fromUsers ?? (delegatableSenders as EmailUser[]) ?? [],
    [fromUsers, delegatableSenders]
  );
  /**
   * What the From menu may offer: the senders, and nobody else.
   *
   * `savedUsers` is a label list — the records already on the form, so a value
   * outside the fetched page still renders a name (ENG-3766) — and merging it
   * in here quietly promoted it to a permission list. On the interview plan it
   * also carries the activity's cc and bcc participants, so the menu offered
   * recipients as senders; and a sender saved before their mailbox was
   * disconnected stayed pickable afterwards. Either one is refused with
   * "Sender's mailbox is not connected" — a 422 raised only once the whole
   * email has been written (ENG-3980).
   *
   * `senders` is the authority. `/users?scope=email_delegatable` returns the
   * account in one unpaginated list, already narrowed server-side to users
   * with a connected mailbox, plus the shared mailboxes this user may send
   * from. The saved records still reach `fromOptions` below, which is what
   * labels the selected value — so dropping them here takes them out of the
   * menu without blanking the row on a saved activity.
   *
   * Still deduped by value: shared mailboxes and delegatable users are
   * concatenated upstream, and the same person must not appear twice
   * (ENG-3945).
   */
  const senderBaseOptions = useMemo(
    () => mergeOptionsByValue(toSelectOptions(senders, valueKey)),
    [senders, valueKey]
  );
  const { fromSelectProps, searchedSenders } = useSenderAutocomplete(
    senderBaseOptions,
    valueKey
  );
  const knownSenders = useMemo(
    () => [...senders, ...(searchedSenders as EmailUser[])],
    [senders, searchedSenders]
  );

  const {
    value,
    handleChange,
    fromOptions,
    submitAttempt: submitCount,
  } = useEmailComposer({
    formik,
    toValue,
    toPatch,
    fromUsers: knownSenders,
    ccBccUsers: ccBccUsers ?? (allUsers as EmailUser[]),
    savedUsers,
    valueKey,
    applyPatch,
    seedKey,
    normalise,
    onTemplateChange,
    wantedFrom,
    selectedTemplate,
  });

  // `submitAttempt` only re-fires scroll/focus; `validateOnMount` is what makes
  // the organism surface its own inline errors instead of waiting for a blur.
  // Without it a never-focused Subject showed nothing and the surface had to
  // fall back to a toast.
  const attempted = submitAttempt ?? submitCount;

  const editorVariables = useMemo(() => ({ vars: variables }), [variables]);

  const subjectInputProps = useMemo(
    () => (subjectMaxLength ? { maxLength: subjectMaxLength } : undefined),
    [subjectMaxLength]
  );

  // Derive the flag apart from the props object: `value` changes on every
  // keystroke, so rebuilding the object here would hand kula's template
  // `Select` a new identity each time.
  const templateEdited = useMemo(() => {
    const selected = templateOptions?.find(
      (option) => option.value === value.template
    );
    return isTemplateEdited(value, selected);
  }, [templateOptions, value]);

  const mergedTemplateSelectProps = useMemo(
    () =>
      templateOptions
        ? {
            ...templateSelectProps,
            ...customTemplateSelectProps(templateEdited),
          }
        : templateSelectProps,
    [templateSelectProps, templateOptions, templateEdited]
  );

  return (
    <Email
      showFrom
      showSubject
      showEditor
      enableCcBcc
      showTo={!!toOptions?.length}
      toMode={toMode}
      showTemplate={!!templateOptions}
      applyTemplateBodyToEditor={!!templateOptions}
      templateOptions={templateOptions}
      {...(mergedTemplateSelectProps
        ? { templateSelectProps: mergedTemplateSelectProps }
        : {})}
      value={value}
      onChange={handleChange}
      fromOptions={fromOptions}
      fromSelectProps={fromSelectProps}
      toOptions={toOptions}
      ccBccOptions={searchedCcBccOptions}
      ccSelectProps={ccBccSelectProps}
      bccSelectProps={ccBccSelectProps}
      submitAttempt={attempted}
      validateOnMount={attempted > 0}
      onInlineErrorChange={onInlineErrorChange}
      subjectVariableInputProps={subjectInputProps}
      disabled={disabled}
      placeholders={PLACEHOLDERS}
      footerAddon={footerAddon}
      editorProps={{
        placeholder: editorPlaceholder,
        // Pasted/inserted images POST to `/user_image_uploads` and the editor keeps the
        // returned URL; without it the body stores a base64 data URL.
        uploadInlineImage: uploadUserInlineImage,
        toolbarFeatures,
        variable: editorVariables,
        extraExtensions,
        toolbarStartSlot,
        onEditorReady,
        ...(attachments ?? {}),
      }}
    />
  );
};
